import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { APITypes, Dynamo } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { useClearInMemoryAvatarCache } from '../useAvatar';
import { appAPI } from '../../utils/api';
import { getUserCacheKey } from './cacheKeys';
import useQueryCallbacks, { useQueryErrorNotify } from '../useQueryCallbacks';
import { maybeStartBugsnagUserSession } from '../../utils/bugsnag';
import { useIsE2E } from '../useIsE2E';
import { useEnvironment } from '../useEnvironment';

const intl = getIntl('useUser', {
    updateSuccess: 'User updated successfully',
    updateFailure: 'User update failed',
    userLoadFailure: 'Failed to load user',
    promoCodeUpdateFailure: 'Failed to update promo code',
    deleteSuccess: 'User deleted successfully',
    deleteFailure: 'User deletion failed',
});

export type UseUserArgs = {
    onUpdateSuccess?: () => void;
    onDeleteSuccess?: () => void;
    noUpdateMessage?: boolean;
};

export type AvatarFileData = {
    name: string;
    base64: string;
};

export type User = APITypes.userService.User;

export type UseUserReturnValue = {
    user: APITypes.userService.MeResponse | 'loading';
    isUpdatingUser: boolean;
    updateUser: (user: APITypes.userService.UpdateUserBody) => void;
    deleteUser: () => void;
    isDeletingUser: boolean;
};

export const isUserPendingRegistration = (user: APITypes.userService.MeResponse): boolean => {
    return user.promptRegistration;
};

export default function useUser({
    onUpdateSuccess,
    noUpdateMessage,
    onDeleteSuccess,
}: UseUserArgs = {}): UseUserReturnValue {
    const isE2E = useIsE2E();
    const clearInMemoryAvatarCache = useClearInMemoryAvatarCache();
    const queryClient = useQueryClient();
    const notify = useNotify();
    const environment = useEnvironment();
    const userQuery = useQuery({
        queryKey: getUserCacheKey(),
        queryFn: async () => {
            return await appAPI.get(urls.api.users.me);
        },
        staleTime: 10 * 60 * 1000,
    });
    useQueryCallbacks({
        query: userQuery,
        onSuccess: (data: APITypes.userService.MeResponse) => {
            maybeStartBugsnagUserSession({
                user: data,
                isE2E,
                environment: environment === 'loading' ? 'prod' : environment,
            });
        },
    });
    useQueryErrorNotify(userQuery, intl('userLoadFailure'));

    const updateUserMutation = useMutation({
        mutationFn: async (user: APITypes.userService.UpdateUserBody) => {
            return await appAPI.put(urls.api.users.me, {
                body: {
                    ...user,
                    // The first of these updates completes registration and is what builds the
                    // new-user notification. A social sign-up sends no platform through Cognito —
                    // signInWithRedirect has nowhere to put it — so this request is the only place
                    // it can come from.
                    clientInfo: { platform: Platform.OS, appVersion: Constants.expoConfig?.version },
                },
            });
        },
        onSuccess: (data) => {
            queryClient.setQueryData<APITypes.userService.User>(getUserCacheKey(), data as any);

            !noUpdateMessage && notify('success', intl('updateSuccess'));
            onUpdateSuccess?.();
            clearInMemoryAvatarCache(urls.api.users.avatar);
        },
        onError: () => {
            notify('error', intl('updateFailure'));
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: async () => {
            await appAPI.del(urls.api.users.me);
        },
        onSuccess: () => {
            notify('success', intl('deleteSuccess'));
            onDeleteSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteFailure'));
        },
    });

    return {
        user: userQuery.isLoading ? 'loading' : userQuery.data,
        updateUser: updateUserMutation.mutate,
        isUpdatingUser: updateUserMutation.isPending,
        deleteUser: deleteUserMutation.mutate,
        isDeletingUser: deleteUserMutation.isPending,
    };
}

export type UseLoadedUserReturnValue = Omit<UseUserReturnValue, 'user'> & {
    user: APITypes.userService.MeResponse;
};

/**
 * Same as useUser, but throws an error if the user is still loading.  If within the app, it's guaranteed that a user
 * will be loaded.  This avoids unnecessary and annoying load checks.
 */
export const useLoadedUser = (args: UseUserArgs): UseLoadedUserReturnValue => {
    const { user, ...rest } = useUser(args);

    if (user === 'loading') throw new Error('User is loading');

    return {
        user,
        ...rest,
    };
};

export const useUserType = (): Dynamo.UserType => {
    const user = useLoadedUser({});

    return user.user.type || 'consumer';
};
